// Survey lifecycle action handlers (publish, pause, resume, restart, finish).
// Each calls a backend endpoint, closes its dialog, and reloads the page.
// Pause/Finish close eagerly; Publish/Resume/Restart close on success only.
// PAYMENT_REQUIRED renders a specific "No credits" toast.

import { toast } from "react-toastify";

import { fetcherSimple, fetcherFull } from "utils/api";
import { PAYMENT_REQUIRED } from "store/auth-status";
import {
    localDatetimeInputToUtc,
    isValidDatetimeInput,
} from "../date-helpers";

// Survey lifecycle transitions (publish/pause/resume/restart/finish)
// change permissions, available actions, and credits. Targeted SWR
// invalidation would require enumerating 8+ cache keys (see F22).
// Reload is the interim approach; revisit after sub-project 2 lands a
// unified cache invalidation story.
function reloadAfterLifecycleTransition() {
    window.location.reload();
}

function handleSurveyActionError(err, closeDialog, actionVerb) {
    closeDialog();
    if (err.message?.includes(PAYMENT_REQUIRED)) {
        toast.error(
            `No credits available. Please purchase credits to ${actionVerb} this survey.`,
        );
    } else {
        toast.error(err.message || `Failed to ${actionVerb} survey`);
    }
}

export function useSurveyLifecycle({
    surveyId,
    instantActive,
    setShowPublishDialog,
    setShowPauseDialog,
    setShowResumeDialog,
    setShowRestartDialog,
    setShowFinishDialog,
}) {
    const handlePublishDialogSubmit = async () => {
        try {
            const response = await fetcherSimple(
                instantActive
                    ? `/v2/survey/${surveyId}/activate`
                    : `/v2/survey/${surveyId}/publish`,
                "GET",
            );
            if (response?.name) {
                setShowPublishDialog(false);
                reloadAfterLifecycleTransition();
            }
        } catch (err) {
            handleSurveyActionError(
                err,
                () => setShowPublishDialog(false),
                "activate",
            );
        }
    };

    const handlePauseDialogSubmit = async () => {
        setShowPauseDialog(false);
        try {
            const response = await fetcherSimple(
                `/v2/survey/${surveyId}/pause`,
                "GET",
            );
            if (response?.name) {
                reloadAfterLifecycleTransition();
            }
        } catch (err) {
            handleSurveyActionError(err, () => {}, "pause");
        }
    };

    const handleResumeDialogSubmit = async () => {
        try {
            const response = await fetcherSimple(
                `/v2/survey/${surveyId}/resume`,
                "POST",
            );
            if (response?.name) {
                setShowResumeDialog(false);
                reloadAfterLifecycleTransition();
            }
        } catch (err) {
            handleSurveyActionError(
                err,
                () => setShowResumeDialog(false),
                "resume",
            );
        }
    };

    const handleRestartDialogSubmit = async (values) => {
        const updateData = {};

        if (!values.startNow && isValidDatetimeInput(values.start_date)) {
            updateData.start_date = localDatetimeInputToUtc(values.start_date);
        }

        if (isValidDatetimeInput(values.end_date)) {
            updateData.end_date = localDatetimeInputToUtc(values.end_date);
        }

        try {
            const response = await fetcherFull(
                `/v2/survey/${surveyId}/reopen`,
                "POST",
                updateData,
            );

            if (response?.name) {
                if (values.startNow) {
                    try {
                        await fetcherSimple(
                            `/v2/survey/${surveyId}/activate`,
                            "GET",
                        );
                    } catch (activateErr) {
                        toast.error(
                            "Survey reopened but activation failed: " +
                                (activateErr.message || "Unknown error"),
                        );
                    }
                }
                toast.success("Survey restarted successfully!");
                setShowRestartDialog(false);
                reloadAfterLifecycleTransition();
            } else if (response?.error) {
                const msg = response.message
                    || (typeof response.error === "string"
                        ? response.error
                        : "Unknown error");
                toast.error(msg);
            }
        } catch (err) {
            toast.error(
                "Failed to restart survey: " +
                    (err.message || "Unknown error"),
            );
        }
    };

    const handleFinishDialogSubmit = async () => {
        setShowFinishDialog(false);
        try {
            const response = await fetcherSimple(
                `/v2/survey/${surveyId}/finish`,
                "GET",
            );
            if (response?.name) {
                reloadAfterLifecycleTransition();
            }
        } catch (err) {
            handleSurveyActionError(err, () => {}, "finish");
        }
    };

    return {
        handlePublishDialogSubmit,
        handlePauseDialogSubmit,
        handleResumeDialogSubmit,
        handleRestartDialogSubmit,
        handleFinishDialogSubmit,
    };
}
